import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../authContext';
import { theme } from '../lib/theme';

export default function MarketplaceDriverApply() {
  const { session, profile, refresh } = useAuth();
  const navigate = useNavigate();
  const userId = session?.user?.id;

  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    email: profile?.email || session?.user?.email || '',
    city: 'Lagos',
    years_driving: 3,
    license_number: '',
    license_type: 'B',
    has_own_car: true,
    car_make: '',
    car_model: '',
    motivation: '',
  });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [existingStatus, setExistingStatus] = useState(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile?.marketplace_driver_status === 'approved') {
      navigate('/marketplace/driver', { replace: true });
    }
  }, [profile?.marketplace_driver_status, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function checkExisting() {
      if (!userId) {
        setChecking(false);
        return;
      }
      const { data, error: checkErr } = await supabase
        .from('driver_applications')
        .select('status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (checkErr) {
        setError(checkErr.message);
      } else {
        setExistingStatus(data?.status || null);
      }
      setChecking(false);
    }
    void checkExisting();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  async function submit(e) {
    e.preventDefault();
    if (!userId) return;
    if (existingStatus === 'pending') {
      setError('You already have a pending application.');
      return;
    }
    if (existingStatus === 'approved') {
      navigate('/marketplace/driver', { replace: true });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: err } = await supabase.from('driver_applications').insert({
        user_id: userId,
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        city: form.city,
        years_driving: Number(form.years_driving),
        license_number: form.license_number,
        license_type: form.license_type,
        has_own_car: form.has_own_car,
        car_make: form.car_make || null,
        car_model: form.car_model || null,
        motivation: form.motivation || null,
        status: 'pending',
      });
      if (err) throw err;
      setExistingStatus('pending');
      setDone(true);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.muted }}>Loading…</p>
      </div>
    );
  }

  if (existingStatus === 'pending' || done) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <h2 style={{ fontWeight: 800 }}>Application submitted</h2>
          <p style={{ color: theme.muted, lineHeight: 1.6 }}>We will review your application and email you when approved.</p>
          <Link to="/" style={{ display: 'inline-block', marginTop: 24, color: theme.accentLight }}>Back home</Link>
        </div>
      </div>
    );
  }

  if (existingStatus === 'rejected') {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <h2 style={{ fontWeight: 800 }}>Application not approved</h2>
          <p style={{ color: theme.muted, lineHeight: 1.6 }}>Your previous application was not approved. Contact support if you believe this is an error.</p>
          <Link to="/" style={{ display: 'inline-block', marginTop: 24, color: theme.accentLight }}>Back home</Link>
        </div>
      </div>
    );
  }

  const inp = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    background: theme.bg1,
    color: theme.text,
    marginBottom: 12,
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font, padding: '40px 24px' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <Link to="/" style={{ color: theme.text, textDecoration: 'none', fontWeight: 700 }}>Transport Team</Link>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginTop: 24, marginBottom: 8 }}>Apply to drive</h1>
        <p style={{ color: theme.muted, marginBottom: 24, fontSize: 14 }}>Marketplace driver application</p>
        {!userId && (
          <p style={{ color: theme.gold, marginBottom: 16 }}>
            <Link to="/marketplace/register?role=driver" style={{ color: theme.accentLight }}>Sign up</Link> before applying.
          </p>
        )}
        <form onSubmit={submit}>
          <input required value={form.full_name} onChange={set('full_name')} placeholder="Full name" style={inp} />
          <input required value={form.phone} onChange={set('phone')} placeholder="Phone" style={inp} />
          <input required type="email" value={form.email} onChange={set('email')} placeholder="Email" style={inp} />
          <input required value={form.license_number} onChange={set('license_number')} placeholder="License number" style={inp} />
          <input required value={form.license_type} onChange={set('license_type')} placeholder="License type" style={inp} />
          <textarea value={form.motivation} onChange={set('motivation')} placeholder="Why do you want to join?" rows={4} style={{ ...inp, resize: 'vertical' }} />
          {error && <p style={{ color: '#f87171' }}>{error}</p>}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: 14, background: theme.accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
            {loading ? 'Submitting…' : 'Submit application'}
          </button>
        </form>
      </div>
    </div>
  );
}
